import React, { ChangeEvent, useRef, useState } from "react";

import { useTheme } from "styled-components";

import { Flex, ParagraphMd } from "@olist/design-system";
import {
  AlertTriangleOutline,
  UploadOutline,
} from "@olist/design-system-icons";

import { HiddenInput, InputWrapper, Note } from "./styles";

interface IFileUploadProps {
  handleFormDataChange: (data: Record<string, any>) => void;
  buttonPosition?: "end" | "start";
}

const SIZE_LIMIT = 10 * 1024 * 1024;

export function FileUpload({
  buttonPosition,
  handleFormDataChange,
}: IFileUploadProps) {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<
    { filename: string; size: string }[]
  >([]);
  const theme = useTheme();

  const onUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setFormErrors((currentState) =>
      currentState.filter((error) => error !== "fileUpload")
    );
    setUploadedFiles([]);

    const { files } = e.target as HTMLInputElement;
    if (!files || !files.length) return null;

    const allowedFileTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
      "text/plain",
      "text/csv",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const validFiles: { filename: string; size: string; rawSize: number }[] =
      [];
    const fileDataArray: any[] = [];

    Array.from(files).forEach((file) => {
      if (!allowedFileTypes.includes(file.type)) {
        setFormErrors((prev) => [...prev, "fileUploadTypes"]);
        return null;
      }

      if (file.size > SIZE_LIMIT) {
        setFormErrors((prev) => [...prev, "fileUploadSize"]);
        return null;
      }

      const fileSizeInKB = file.size / 1024;
      const fileSizeFormatted =
        fileSizeInKB < 1024
          ? `${fileSizeInKB.toFixed(2)} KB`
          : `${(fileSizeInKB / 1024).toFixed(2)} MB`;

      validFiles.push({
        filename: file.name,
        size: fileSizeFormatted,
        rawSize: fileSizeInKB,
      });

      if (validFiles.reduce((acc, cur) => cur.rawSize + acc, 0) > SIZE_LIMIT) {
        setFormErrors((prev) => [...prev, "fileUploadSize"]);
        return null;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        const fileData = {
          filename: file.name,
          base64: base64.split(",")[1],
          filetype: file.type,
        };
        fileDataArray.push(fileData);

        if (fileDataArray.length === validFiles.length) {
          handleFormDataChange({ fileUpload: fileDataArray });
        }
      };
      reader.readAsDataURL(file);
      return null;
    });

    setUploadedFiles(validFiles);
    return null;
  };

  const handleInputClick = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  };

  return (
    <Flex flexDirection="column" alignItems={buttonPosition}>
      <InputWrapper onClick={handleInputClick}>
        <UploadOutline
          width="18"
          height="18"
          style={{
            backgroundColor: theme.text?.accent,
          }}
        />
        <ParagraphMd bold color={theme.text?.primary}>
          procurar arquivos
        </ParagraphMd>
        <HiddenInput
          type="file"
          ref={uploadInputRef}
          onChange={onUpload}
          multiple
        />
      </InputWrapper>

      {uploadedFiles.length > 0 ? (
        uploadedFiles.map((file) => (
          <Note
            key={file.filename + file.size}
            style={{ color: theme.text?.secondary }}
          >
            Arquivo carregado: {file.filename} ({file.size})
          </Note>
        ))
      ) : (
        <Note style={{ color: theme.text?.secondary }}>
          O tamanho total de todos os arquivos não deve ultrapassar 10Mb
        </Note>
      )}

      {formErrors.includes("fileUpload") && (
        <Flex alignItems="center" marginTop="8px">
          <AlertTriangleOutline
            color={theme.text?.accent}
            style={{ marginLeft: "18px" }}
          />
          <ParagraphMd
            color="alert.base"
            style={{ color: theme.text?.accent, marginLeft: "8px" }}
          >
            Ocorreu um erro ao fazer o upload de um ou mais arquivos.
          </ParagraphMd>
        </Flex>
      )}
      {formErrors.includes("fileUploadTypes") && (
        <Flex alignItems="center" marginTop="8px">
          <AlertTriangleOutline
            color={theme.text?.accent}
            style={{ marginLeft: "18px" }}
          />
          <ParagraphMd
            color="alert.base"
            style={{ color: theme.text?.accent, marginLeft: "8px" }}
          >
            Ocorreu um erro ao fazer o upload: formato não suportado, tente
            outra extensão.
          </ParagraphMd>
        </Flex>
      )}
      {formErrors.includes("fileUploadSize") && (
        <Flex alignItems="center" marginTop="8px">
          <AlertTriangleOutline
            color={theme.text?.accent}
            style={{ marginLeft: "18px" }}
          />
          <ParagraphMd
            color="alert.base"
            style={{ color: theme.text?.accent, marginLeft: "8px" }}
          >
            Ocorreu um erro ao fazer o upload: o total dos arquivos supera 10MB.
          </ParagraphMd>
        </Flex>
      )}
    </Flex>
  );
}
